/**
 * Single source of truth for resolving a user's subscriptions into display rows.
 *
 * Fixes three data-display bugs that the old card UI had:
 *  - Bug A: every receipt showed the same expiry, because the backend reconcile
 *    job overwrites each row's `a_expirytimemillis` with the latest expiry. We
 *    prefer the per-transaction `expirationDate` decoded from purchaseStringified.
 *  - Bug B: an expired short trial showed "N days remaining" computed off that
 *    corrupted expiry. We clamp remaining time and only treat a row as in-trial
 *    when its OWN resolved expiry is in the future.
 *  - Bug C: a Plus ("light") purchase was labelled Pro because the parsed
 *    productId column was wrong. We read the authoritative productId from the
 *    decoded transaction; when we cannot verify it, the row is marked Unverified
 *    instead of silently guessing Pro.
 */

import type { Receipt, Subscription } from "@/common/model/AdminUserDetails";
import type { FreeSubscription } from "@/common/model/FreeSubscription";
import { resolveCountry, type CountryInfo } from "./country";
import { getCancelReasonDoc, type CancelReasonDoc, type ReceiptPlatform } from "./cancelReasons";
import { getPriceConsentDoc, type PriceConsentDoc } from "./priceConsent";

export type SubSource = "store" | "stripe" | "free_grant";
export type SubTier = "pro" | "plus" | "trial" | "free" | "unknown";
export type RowStatus = "active" | "superseded" | "expired" | "trial_expired" | "deleted" | "unknown";

export interface UnifiedSubRow {
    key: string;
    source: SubSource;
    tier: SubTier;
    /** Friendly product label, e.g. "Yearly Pro", "Yearly Plus", "Free grant (Pro)". */
    productLabel: string;
    rawProductId: string | null;
    platform: ReceiptPlatform;
    platformLabel: string;
    purchaseDate: number | null;
    expiryDate: number | null;
    /** True only when expiry is genuinely in the future AND this is a trial. */
    isActiveTrial: boolean;
    status: RowStatus;
    reference: string | null;
    referenceLabel: "Order #" | "Granted by" | null;
    country: CountryInfo | null;
    cancelReason: CancelReasonDoc | null;
    priceConsent: PriceConsentDoc | null;
    /** Formatted price the user paid, e.g. "€3.99" / "Free trial", or null when unknown. */
    price: string | null;
    /** Numeric price in currency units (e.g. 3.99), or null when unknown. 0 = free trial. For stats. */
    priceUnits: number | null;
    /** ISO-4217 currency for priceUnits, or null. */
    priceCurrency: string | null;
    /** Whether tier/expiry came from a trustworthy decoded source. */
    verified: boolean;
    observations?: string | null;
    fromMigration?: boolean;
    deletedAt?: number | null;
    /** Raw payload for the expandable detail view. */
    raw: Receipt | FreeSubscription | StripeSubscriptionRow;
}

interface DecodedTxn {
    productId?: string;
    expirationDate?: number;
    purchaseDate?: number;
    storefront?: string;
    revocationDate?: number | null;
    revocationReason?: number | null;
    isTrial?: boolean;
    offerType?: string | number | null;
    /** iOS price in milliunits (e.g. 3990 = 3.99); 0 = free trial. */
    price?: number;
    /** iOS ISO-4217 currency, e.g. EUR. */
    currency?: string;
}

const PLATFORM_LABELS: Record<ReceiptPlatform, string> = {
    ios: "Apple",
    android: "Google",
    web: "Website",
    unknown: "—",
};

export function normalizePlatform(platform: string | null | undefined, productId?: string | null): ReceiptPlatform {
    const p = (platform || "").toLowerCase();
    if (p.includes("ios") || p.includes("apple")) return "ios";
    if (p.includes("android") || p.includes("google")) return "android";
    if (p.includes("web") || p.includes("website")) return "web";
    // Fall back to product-id hints when platform is absent.
    if (productId && productId.toLowerCase().includes("android")) return "android";
    return "unknown";
}

/**
 * Tier from an authoritative productId.
 * - "light_*" / "*plus*" => Plus.
 * - Known Pro SKU families (annual_subscription*, monthly_subscription*, explicit *pro*) => Pro.
 * - Anything else (a new/unrecognized SKU) => unknown, so it surfaces as "Unverified"
 *   rather than being silently guessed as Pro. (codex/agy review: never guess Pro.)
 */
function tierFromProductId(productId: string | null | undefined): SubTier {
    if (!productId) return "unknown";
    const p = productId.toLowerCase();
    if (p.includes("light") || p.includes("plus")) return "plus";
    // Known Pro SKUs only — do NOT treat a bare "subscription" token as Pro.
    if (
        p.includes("pro") ||
        p.includes("annual_subscription") ||
        p.includes("monthly_subscription") ||
        p.includes("yearly_subscription")
    ) {
        return "pro";
    }
    return "unknown";
}

function cadence(productId: string | null | undefined): string {
    if (!productId) return "";
    const p = productId.toLowerCase();
    if (p.includes("monthly")) return "Monthly ";
    if (p.includes("annual") || p.includes("yearly")) return "Yearly ";
    return "";
}

/**
 * The decoded transaction is stored as an escaped JSON string nested inside the
 * purchaseStringified envelope. Pull out the authoritative fields defensively —
 * the envelope shape drifts across store/library versions.
 */
export function parseDecodedTxn(purchaseStringified: string | null | undefined): DecodedTxn | null {
    if (!purchaseStringified) return null;
    const s = purchaseStringified;

    const numAfter = (key: string): number | undefined => {
        // Matches "key":123  and  \"key\":123  (escaped, nested JSON-in-JSON).
        const re = new RegExp(`\\\\?"${key}\\\\?"\\s*:\\s*(\\d{6,})`);
        const m = s.match(re);
        return m ? Number(m[1]) : undefined;
    };
    const strAfter = (key: string): string | undefined => {
        // Allow the full set of chars real product IDs / storefronts use: letters,
        // digits, underscore, dot, hyphen (codex review: SKUs often contain . and -).
        const re = new RegExp(`\\\\?"${key}\\\\?"\\s*:\\s*\\\\?"([A-Za-z0-9_.-]+)\\\\?"`);
        const m = s.match(re);
        return m ? m[1] : undefined;
    };

    // Small non-negative integer (e.g. price in milliunits: 3990, or 0 for a free trial).
    const smallNumAfter = (key: string): number | undefined => {
        const re = new RegExp(`\\\\?"${key}\\\\?"\\s*:\\s*(\\d+)`);
        const m = s.match(re);
        return m ? Number(m[1]) : undefined;
    };

    // Prefer the nested-representation lowercase keys (StoreKit2), but fall back to the OUTER
    // capitalized keys (productID / appBundleID-era blobs whose jsonRepresentation is null).
    // Without this, older single-object transaction blobs resolve to "Unverified". (Victoria #6102.)
    const productId = strAfter("productId") ?? strAfter("productID");
    const storefront = strAfter("storefront");
    const expirationDate = numAfter("expirationDate") ?? numAfter("expiresDate");
    const purchaseDate = numAfter("purchaseDate");
    const price = smallNumAfter("price"); // iOS: milliunits
    const currency = strAfter("currency");

    if (productId == null && expirationDate == null && storefront == null && price == null) return null;
    return { productId, expirationDate, purchaseDate, storefront, price, currency };
}

/**
 * Resolve the numeric price the user paid. iOS price comes from the decoded blob in MILLIUNITS
 * (3990 = 3.99); Android from priceAmountMicros in MICROS (6990000 = 6.99). 0 = free trial.
 * units is null when no price signal exists (older iOS receipts predate the field; some grants).
 */
function resolvePrice(receipt: Receipt, decoded: DecodedTxn | null, platform: ReceiptPlatform): { units: number | null; currency: string | null } {
    if (platform === "android" && receipt.priceAmountMicros != null) {
        return { units: receipt.priceAmountMicros / 1_000_000, currency: receipt.priceCurrencyCode ?? null };
    }
    if (decoded?.price != null) {
        return { units: decoded.price / 1_000, currency: decoded.currency ?? null };
    }
    return { units: null, currency: null };
}

/** Format a numeric price for display, e.g. "€3.99" / "Free trial" / null. */
export function formatMoney(units: number | null, currency: string | null): string | null {
    if (units == null) return null;
    if (units === 0) return "Free trial";
    if (!currency) return units.toFixed(2);
    try {
        return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(units);
    } catch {
        return `${units.toFixed(2)} ${currency}`; // unknown currency code → plain
    }
}

function rowStatus(expiry: number | null, now: number, isTrial: boolean): RowStatus {
    if (expiry == null) return "unknown";
    if (expiry > now) return "active";
    return isTrial ? "trial_expired" : "expired";
}

/** Map a store Receipt (+ optional matching Subscription) to a unified row. */
export function receiptToRow(
    receipt: Receipt,
    subscription: Subscription | undefined,
    now: number
): UnifiedSubRow {
    const decoded = parseDecodedTxn(receipt.purchaseStringified);
    const platform = normalizePlatform(receipt.platform ?? subscription?.platform, receipt.productId);

    // Bug C: prefer the authoritative decoded productId over the parsed column.
    const authoritativeProductId = decoded?.productId ?? null;
    const displayProductId = authoritativeProductId ?? receipt.productId ?? null;

    // We "verify" iOS from decoded JSON; Android has reliable parsed columns even
    // without a decoded productId, so treat a present platform+productId as verified.
    const verified = authoritativeProductId != null || (platform === "android" && !!receipt.productId);

    let tier: SubTier = tierFromProductId(authoritativeProductId ?? (verified ? receipt.productId : null));
    if (!verified) tier = "unknown";

    // Bug A: per-row expiry from the decoded txn. The parsed column (receipt.expiryTimeMillis) is
    // reconcile-overwritten to a uniform value on iOS, so we do NOT trust it when a decoded blob
    // EXISTS but lacks its own expiry (that's the corruption case → "Expiry unknown"). But when
    // there is NO decoded blob at all (decoded === null, e.g. reconcile nulled purchasestringified),
    // the parsed column is the only signal we have, so fall back to it (the row stays Unverified).
    // Android's column is authoritative regardless. (codex review + Victoria #6102.)
    const columnExpiry = receipt.expiryTimeMillis ?? subscription?.expirytime ?? null;
    const columnExpiryFallback = platform === "android" || decoded === null ? columnExpiry : null;
    const expiryDate = decoded?.expirationDate ?? columnExpiryFallback ?? null;
    const purchaseDate = decoded?.purchaseDate ?? receipt.purchaseTime ?? subscription?.purchasetime ?? null;

    // Bug B: trial only counts as "active trial" when its own expiry is in the future.
    const flaggedTrial = !!subscription?.isfreetrial;
    const isActiveTrial = flaggedTrial && expiryDate != null && expiryDate > now;
    if (isActiveTrial) tier = "trial";

    // Country: Android from countryCode, iOS from decoded storefront.
    const country = resolveCountry(receipt.countryCode ?? decoded?.storefront);

    const cancelReason = getCancelReasonDoc(platform, receipt.cancelReason);
    const priceConsent = getPriceConsentDoc(receipt.priceConsentStatus);
    const priceInfo = resolvePrice(receipt, decoded, platform);
    // Status reflects time only (active vs expired vs trial_expired). We deliberately do NOT
    // remap to "refunded" from the cancel-reason code: "Replaced" (Google reason 2) is a plan
    // change, not a refund, and conflating them shows a misleading pill. The precise reason is
    // surfaced in its own column + tooltip. (Copilot review.)
    const status = rowStatus(expiryDate, now, flaggedTrial);

    const tierWord = tier === "plus" ? "Plus" : tier === "pro" ? "Pro" : tier === "trial" ? "Trial" : "Unverified";
    const productLabel = verified ? `${cadence(displayProductId)}${tierWord}`.trim() : "Unverified — revalidate";

    return {
        key: `receipt-${receipt.id}`,
        source: "store",
        tier,
        productLabel,
        rawProductId: displayProductId,
        platform,
        platformLabel: PLATFORM_LABELS[platform],
        purchaseDate,
        expiryDate,
        isActiveTrial,
        status,
        reference: receipt.orderId ?? subscription?.orderid ?? null,
        referenceLabel: "Order #",
        country,
        cancelReason,
        priceConsent,
        price: formatMoney(priceInfo.units, priceInfo.currency),
        priceUnits: priceInfo.units,
        priceCurrency: priceInfo.currency,
        verified,
        raw: receipt,
    };
}

/** Map a free-grant subscription to a unified row. */
export function freeGrantToRow(grant: FreeSubscription, now: number): UnifiedSubRow {
    const start = grant.startDate ? new Date(grant.startDate).getTime() : null;
    const end = grant.endDate ? new Date(grant.endDate).getTime() : null;
    const deletedAt = grant.deletedAt ? new Date(grant.deletedAt).getTime() : null;

    const status: RowStatus = deletedAt != null ? "deleted" : end != null && end < now ? "expired" : "active";
    const grantTier = (grant.subscriptionType || "pro").toLowerCase();
    const tier: SubTier = grantTier === "plus" ? "plus" : "pro";

    return {
        key: `free-${grant.id}`,
        source: "free_grant",
        tier,
        productLabel: `Free grant (${tier === "plus" ? "Plus" : "Pro"})`,
        rawProductId: null,
        platform: "unknown",
        platformLabel: "Free grant",
        purchaseDate: start,
        expiryDate: end,
        isActiveTrial: false,
        status,
        reference: grant.grantedByUserName ?? (grant.grantedBy != null ? String(grant.grantedBy) : null),
        referenceLabel: "Granted by",
        country: null,
        cancelReason: null,
        priceConsent: null,
        price: null,
        priceUnits: null,
        priceCurrency: null,
        verified: true,
        observations: grant.observations,
        fromMigration: grant.fromMigration,
        deletedAt,
        raw: grant,
    };
}

/**
 * One Stripe-billed subscription, as returned by GET /free-subscription/stripe[?userId=].
 * Stripe rows are excluded from /free-subscription/all (they are not admin-grantable), so
 * they arrive on their own channel and are merged in here — without this a Stripe-paying
 * rider shows no subscription at all in the PRO Details tab.
 */
export interface StripeSubscriptionRow {
    id: number;
    userId: number;
    userName: string | null;
    subscriptionType: string | null;
    startDate: string | null;
    endDate: string | null;
    active: boolean;
    stripeSubscriptionId: string | null;
    stripeCustomerId: string | null;
    dashboardUrl: string | null;
    cancelState: string | null;
    cancelReasonCode: string | null;
    cancelEffectiveAt: string | null;
    providerStatus: string | null;
}

/** Normalized cancel-reason codes -> admin-facing copy, shared across providers. */
const CANCEL_REASON_LABELS: Record<string, { label: string; explainer: string; tone: "neutral" | "warn" | "info" }> = {
    user_requested: {
        label: "User canceled",
        explainer: "The rider cancelled the subscription themselves in the Stripe billing portal. Voluntary churn.",
        tone: "neutral",
    },
    payment_failed: {
        label: "Payment failed",
        explainer:
            "Stripe could not collect payment and gave up after dunning. Involuntary churn — a good win-back / payment-recovery target.",
        tone: "warn",
    },
    unknown: {
        label: "Unknown reason",
        explainer: "Stripe reported a cancellation reason we do not map yet. The raw value is stored on the row.",
        tone: "info",
    },
};

export function stripeSubToRow(sub: StripeSubscriptionRow, now: number): UnifiedSubRow {
    const start = sub.startDate ? new Date(sub.startDate).getTime() : null;
    const end = sub.endDate ? new Date(sub.endDate).getTime() : null;

    // 'light' is the legacy stored name for the product sold as Plus.
    const raw = (sub.subscriptionType || "").toLowerCase();
    const tier: SubTier = raw === "pro" ? "pro" : raw === "light" || raw === "plus" ? "plus" : "unknown";
    const tierLabel = tier === "pro" ? "PRO" : tier === "plus" ? "Plus" : "Unknown";

    // "pending_cancel" is cancelled but STILL ENTITLED to term, so it must not read as
    // expired: the distinction is exactly what this feature exists to make visible.
    const status: RowStatus =
        end != null && end < now ? "expired" : sub.active || sub.cancelState === "pending_cancel" ? "active" : "expired";

    const cancelReason = sub.cancelReasonCode ? CANCEL_REASON_LABELS[sub.cancelReasonCode] ?? null : null;

    return {
        key: `stripe-${sub.id}`,
        source: "stripe",
        tier,
        productLabel: `Stripe ${tierLabel}`,
        rawProductId: null,
        platform: "web",
        platformLabel: "Stripe",
        purchaseDate: start,
        expiryDate: end,
        isActiveTrial: sub.providerStatus === "trialing",
        status,
        reference: sub.stripeSubscriptionId,
        referenceLabel: "Order #",
        country: null,
        cancelReason,
        priceConsent: null,
        // Stripe amounts are not stored locally (ADR: no forecast/price duplication); the
        // dashboard link is the source of truth for what was actually charged.
        price: null,
        priceUnits: null,
        priceCurrency: null,
        verified: true,
        observations: cancelStateNote(sub),
        fromMigration: false,
        deletedAt: null,
        raw: sub,
    };
}

/** Human note for the row's Details view: makes "cancelled but running" unmistakable. */
function cancelStateNote(sub: StripeSubscriptionRow): string | undefined {
    if (sub.cancelState === "pending_cancel") {
        const until = sub.cancelEffectiveAt ?? sub.endDate;
        return until
            ? `Cancelled — access continues until ${new Date(until).toLocaleDateString()}`
            : "Cancelled — access continues until the end of the period";
    }
    if (sub.cancelState === "canceled") return "Cancelled — access ended";
    if (sub.providerStatus === "past_due") return "Payment overdue — Stripe is retrying";
    return undefined;
}

export interface EffectiveEntitlement {
    tier: SubTier;
    source: SubSource | null;
    expiryDate: number | null;
    verified: boolean;
    /** Set when both a store sub and a free grant are simultaneously valid. */
    overlapNote: string | null;
}

const TIER_RANK: Record<SubTier, number> = { unknown: 0, free: 0, trial: 1, plus: 2, pro: 3 };

/** Billing-source precedence, mirroring the backend's GeneralSettingsService.subscriptionSource. */
const SOURCE_PRIORITY: SubSource[] = ["store", "stripe", "free_grant"];

const SOURCE_LABELS: Record<SubSource, string> = {
    store: "store purchase",
    stripe: "Stripe subscription",
    free_grant: "free grant",
};

/**
 * Compute the user's CURRENT entitlement from the resolved rows.
 *
 * Rule: the highest-precedence source wins (store > stripe > free grant), unless a
 * lower-precedence source grants a strictly HIGHER tier — a comped PRO must outrank a paid
 * Plus, otherwise support sees the wrong entitlement.
 *
 * Iterating over every source (rather than naming two) is deliberate: the previous version
 * hardcoded `store ?? grant` and threw "Cannot read properties of undefined (reading 'tier')"
 * the moment a rider's only active row was a Stripe one — a white-screen crash on the whole
 * PRO Details tab. Adding a fourth source later must not be able to reintroduce that.
 */
export function computeEffectiveEntitlement(rows: UnifiedSubRow[], now: number): EffectiveEntitlement {
    const activeNow = rows.filter((r) => r.status === "active" && (r.expiryDate == null || r.expiryDate > now));

    if (activeNow.length === 0) {
        return { tier: "free", source: null, expiryDate: null, verified: true, overlapNote: null };
    }

    // Best (latest-expiring) active row per source.
    const bySource = new Map<SubSource, UnifiedSubRow>();
    for (const source of SOURCE_PRIORITY) {
        const best = activeNow
            .filter((r) => r.source === source)
            .sort((a, b) => (b.expiryDate ?? 0) - (a.expiryDate ?? 0))[0];
        if (best) bySource.set(source, best);
    }

    const present = SOURCE_PRIORITY.filter((s) => bySource.has(s));
    // Defensive: a row whose source is outside SOURCE_PRIORITY would otherwise leave this
    // empty and crash exactly like the bug above. Fall back to the latest-expiring row.
    if (present.length === 0) {
        const fallback = [...activeNow].sort((a, b) => (b.expiryDate ?? 0) - (a.expiryDate ?? 0))[0];
        return {
            tier: fallback.tier,
            source: fallback.source,
            expiryDate: fallback.expiryDate,
            verified: fallback.verified,
            overlapNote: null,
        };
    }

    let winner = bySource.get(present[0]) as UnifiedSubRow;
    for (const source of present.slice(1)) {
        const candidate = bySource.get(source) as UnifiedSubRow;
        if (TIER_RANK[candidate.tier] > TIER_RANK[winner.tier]) winner = candidate;
    }

    const others = present.map((s) => bySource.get(s) as UnifiedSubRow).filter((r) => r !== winner);
    const overlapNote =
        others.length > 0
            ? `Also has an active ${others
                  .map((r) => `${SOURCE_LABELS[r.source]} (${r.tier})`)
                  .join(", ")}; ${SOURCE_LABELS[winner.source]} is in force.`
            : null;

    return {
        tier: winner.tier,
        source: winner.source,
        expiryDate: winner.expiryDate,
        verified: winner.verified,
        overlapNote,
    };
}

/**
 * A user can hold only ONE active subscription per chain. Within each store renewal/upgrade chain
 * (grouped by Apple originalTransactionId, falling back to the receipt's own order id), only the
 * latest transaction may stay "active". Older transactions that resolved to "active" purely because
 * their own expiry is still in the future (Apple doesn't rewrite a superseded transaction's
 * expirationDate) are demoted to "superseded" — they are historical, not concurrently live.
 */
function collapseChains(storeRows: UnifiedSubRow[]): UnifiedSubRow[] {
    const chainKey = (r: UnifiedSubRow): string => {
        const rc = r.raw as Receipt;
        return rc.originalTransactionId ?? `order:${rc.orderId ?? r.key}`;
    };

    // Find the latest (max purchaseDate, then max receipt id) active row per chain.
    const latestActiveByChain = new Map<string, UnifiedSubRow>();
    for (const r of storeRows) {
        if (r.status !== "active") continue;
        const key = chainKey(r);
        const cur = latestActiveByChain.get(key);
        if (!cur) {
            latestActiveByChain.set(key, r);
            continue;
        }
        const rId = (r.raw as Receipt).id ?? 0;
        const curId = (cur.raw as Receipt).id ?? 0;
        const rNewer = (r.purchaseDate ?? 0) > (cur.purchaseDate ?? 0) || ((r.purchaseDate ?? 0) === (cur.purchaseDate ?? 0) && rId > curId);
        if (rNewer) latestActiveByChain.set(key, r);
    }

    return storeRows.map((r) =>
        r.status === "active" && latestActiveByChain.get(chainKey(r)) !== r
            ? { ...r, status: "superseded" as RowStatus }
            : r
    );
}

/**
 * Drop duplicate receipt rows for the same Apple transaction. A double-insert produces two rows
 * with the same app_transaction_id (or, lacking that, the same orderId). Keep the best one:
 * prefer a row that still has the decoded blob, then the freshest a_lastcheck is unavailable on the
 * FE so fall back to the highest id (latest written). (Victoria #6102 — same txn inserted twice.)
 */
function dedupeReceipts(receipts: Receipt[]): Receipt[] {
    const byTxn = new Map<string, Receipt>();
    const passthrough: Receipt[] = [];
    for (const r of receipts) {
        const key = (r as { app_transaction_id?: string }).app_transaction_id || r.orderId;
        if (!key) {
            passthrough.push(r);
            continue;
        }
        const cur = byTxn.get(key);
        if (!cur) {
            byTxn.set(key, r);
            continue;
        }
        const better =
            (!!r.purchaseStringified && !cur.purchaseStringified) ||
            (!!r.purchaseStringified === !!cur.purchaseStringified && (r.id ?? 0) > (cur.id ?? 0));
        if (better) byTxn.set(key, r);
    }
    return [...Array.from(byTxn.values()), ...passthrough];
}

/** Build all rows for a user, sorted by date descending. Pure + testable. */
export function buildUnifiedRows(
    receipts: Receipt[],
    subscriptions: Subscription[],
    freeGrants: FreeSubscription[],
    now: number,
    stripeSubs: StripeSubscriptionRow[] = []
): UnifiedSubRow[] {
    const storeRows = collapseChains(
        dedupeReceipts(receipts).map((r) => {
            const sub = subscriptions.find((s) => s.orderid === r.orderId);
            return receiptToRow(r, sub, now);
        })
    );
    const grantRows = freeGrants.map((g) => freeGrantToRow(g, now));
    // Stripe rows arrive on their own channel (they are filtered out of /free-subscription/all)
    // and are merged here so the table is the rider's COMPLETE billing picture.
    const stripeRows = stripeSubs.map((s) => stripeSubToRow(s, now));
    return [...storeRows, ...grantRows, ...stripeRows].sort(
        (a, b) => (b.purchaseDate ?? 0) - (a.purchaseDate ?? 0)
    );
}

// ---------------------------------------------------------------------------
// Customer payment insights (UI-only, computed from the resolved rows).
// ---------------------------------------------------------------------------

export type LoyaltyLevel = "New" | "Regular" | "Loyal" | "Champion";

export interface CustomerInsights {
    /** Days since first paid/store purchase, or null if none. */
    tenureDays: number | null;
    /** Count of paid store receipts (excludes trials priced 0 and free grants). */
    paidCount: number;
    /** Total paid per currency, e.g. {EUR: 47.97}. Only across receipts that HAVE a price. */
    totalByCurrency: Record<string, number>;
    /** How many paid receipts actually had a price (vs total paid receipts) — coverage honesty. */
    pricedCount: number;
    loyalty: LoyaltyLevel;
    /** True if the user has any active (non-historical) store/grant sub right now. */
    active: boolean;
    /** When the latest store sub lapsed (ms), if churned. null if active or never paid. */
    churnedAt: number | null;
    /** "upgrade" | "downgrade" | "stable" comparing the two most recent priced receipts (same currency). */
    trend: "upgrade" | "downgrade" | "stable" | null;
}

const DAY = 86_400_000;

export function computeCustomerInsights(rows: UnifiedSubRow[], now: number): CustomerInsights {
    const store = rows.filter((r) => r.source === "store");
    // Paid = store receipts that are not a 0-priced trial. (Receipts with unknown price still count
    // as a paid subscription; they just don't contribute to totals.)
    const paid = store.filter((r) => r.priceUnits == null || r.priceUnits > 0);

    const firstPurchase = store.reduce<number | null>(
        (min, r) => (r.purchaseDate != null && (min == null || r.purchaseDate < min) ? r.purchaseDate : min),
        null
    );
    const tenureDays = firstPurchase != null ? Math.floor((now - firstPurchase) / DAY) : null;

    const totalByCurrency: Record<string, number> = {};
    let pricedCount = 0;
    for (const r of paid) {
        if (r.priceUnits != null && r.priceUnits > 0 && r.priceCurrency) {
            totalByCurrency[r.priceCurrency] = (totalByCurrency[r.priceCurrency] ?? 0) + r.priceUnits;
            pricedCount++;
        }
    }

    const active = rows.some((r) => r.status === "active" && (r.expiryDate == null || r.expiryDate > now));

    // Churn: latest store row that's expired/superseded and not currently active → its expiry.
    let churnedAt: number | null = null;
    if (!active && store.length > 0) {
        churnedAt = store.reduce<number | null>(
            (max, r) => (r.expiryDate != null && (max == null || r.expiryDate > max) ? r.expiryDate : max),
            null
        );
    }

    // Trend: compare the two most recent priced receipts (same currency only, to be meaningful).
    let trend: CustomerInsights["trend"] = null;
    const pricedSorted = paid
        .filter((r) => r.priceUnits != null && r.priceUnits > 0 && r.purchaseDate != null)
        .sort((a, b) => (b.purchaseDate ?? 0) - (a.purchaseDate ?? 0));
    if (pricedSorted.length >= 2 && pricedSorted[0].priceCurrency === pricedSorted[1].priceCurrency) {
        const latest = pricedSorted[0].priceUnits as number;
        const prev = pricedSorted[1].priceUnits as number;
        trend = latest > prev ? "upgrade" : latest < prev ? "downgrade" : "stable";
    }

    // Loyalty: tenure + paid count. Simple, explainable buckets.
    const years = tenureDays != null ? tenureDays / 365 : 0;
    let loyalty: LoyaltyLevel = "New";
    if (paid.length >= 6 || years >= 3) loyalty = "Champion";
    else if (paid.length >= 3 || years >= 1.5) loyalty = "Loyal";
    else if (paid.length >= 2 || years >= 0.5) loyalty = "Regular";

    return { tenureDays, paidCount: paid.length, totalByCurrency, pricedCount, loyalty, active, churnedAt, trend };
}
